import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Heart, ExternalLink, Instagram, Music2, Eye } from 'lucide-react';
import PageHero from '../../components/ui-shree/PageHero';
import SectionLabel from '../../components/ui-shree/SectionLabel';
import ShareMenu from '../../components/share/ShareMenu';
import OrderModal from './OrderModal';
import ApplyCreatorForm from './ApplyCreatorForm';
import {
  fetchSevagramPosts,
  fetchSevagramLeaderboard,
  trackPostEvent,
} from '../../lib/api';

const SevagramPage = () => {
  const [posts, setPosts] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderRef, setOrderRef] = useState(null); // { ref, productSlugs, creatorHandle }
  const [search] = useSearchParams();

  useEffect(() => {
    Promise.all([fetchSevagramPosts({ limit: 30 }), fetchSevagramLeaderboard(5)])
      .then(([p, l]) => {
        setPosts(p);
        setLeaders(l);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Allow ?ref=SEVA-XYZ deep links to auto-open the order modal with that attribution
  useEffect(() => {
    const ref = search.get('ref');
    if (ref) setOrderRef({ ref, productSlugs: [] });
  }, [search]);

  // Track a "view" once posts have rendered
  useEffect(() => {
    if (!posts.length) return;
    posts.forEach((p) => trackPostEvent(p.id, 'view'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length]);

  const onPostClick = (post) => {
    trackPostEvent(post.id, 'click', post.external_url ? 'external' : null);
  };

  return (
    <div data-testid="sevagram-page">
      <PageHero
        overline="Sevagram · Creator Feed"
        title={
          <>
            Beauty, written by the <em className="text-[#90B0BF]">people</em>
            <br />who use it.
          </>
        }
        subtitle="Sevagram is our creator-owned feed. Post a ritual, share your link, and earn $SEVA every time it leads to a sale. Ten percent of every order goes back to the voice that started it."
        testId="sevagram-hero"
      >
        <div className="flex flex-wrap gap-4 mt-2">
          <a
            href="#apply"
            data-testid="sevagram-cta-apply"
            className="bg-[#7C9CB1] text-[#F4F8F9] px-8 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-[#90B0BF] transition-colors duration-300"
          >
            Become a creator
          </a>
          <a
            href="#feed"
            data-testid="sevagram-cta-feed"
            className="border border-[#7C9CB1] text-[#7C9CB1] px-8 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-[#C1D7E2]/40 transition-colors duration-300"
          >
            Browse the feed
          </a>
        </div>
      </PageHero>

      {/* How it works */}
      <section
        data-testid="sevagram-how"
        className="px-6 md:px-12 py-20 md:py-28 bg-[#F4F8F9]"
      >
        <div className="max-w-[1400px] mx-auto">
          <SectionLabel testId="how-label">How Sevagram pays</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              {
                no: '01',
                title: 'Post the ritual',
                body:
                  'Upload a photo or video here, or paste your existing Instagram or TikTok link. Tag the Shreesavaa products you used.',
              },
              {
                no: '02',
                title: 'Share your code',
                body:
                  'Every approved creator gets a permanent ref code (e.g. SEVA-MAYA-7C9C). Add it to your bio, captions, or directly to a post link.',
              },
              {
                no: '03',
                title: 'Earn $SEVA tokens',
                body:
                  'When a viewer orders using your link or code, 10% of the order value is minted as $SEVA into your creator wallet. Permanent, on-chain.',
              },
            ].map((s, i) => (
              <div
                key={s.no}
                data-testid={`how-step-${i}`}
                className="border-t border-[#90B0BF]/40 pt-6"
              >
                <p className="font-mono text-xs tracking-[0.3em] text-[#90B0BF]">
                  {s.no}
                </p>
                <h3 className="mt-3 font-serif text-3xl text-[#7C9CB1] font-light">
                  {s.title}
                </h3>
                <p className="mt-4 text-[#7C9CB1]/85 font-light leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leaderboard */}
      <section
        data-testid="sevagram-leaderboard"
        className="px-6 md:px-12 py-20 md:py-28 bg-[#C1D7E2]/30 border-y border-[#C1D7E2]"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <SectionLabel>The Atelier of Voices</SectionLabel>
              <h2 className="font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-[1.05]">
                This season's leaders.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {leaders.length === 0 ? (
              <p className="font-serif italic text-[#7C9CB1] col-span-full">
                The board is quiet — be the first voice.
              </p>
            ) : (
              leaders.map((c, i) => (
                <article
                  key={c.handle}
                  data-testid={`leader-${i}`}
                  className="bg-[#F4F8F9]/80 border border-[#C1D7E2] p-5 flex flex-col"
                >
                  <div className="flex items-center gap-3">
                    {c.avatar_url ? (
                      <img
                        src={c.avatar_url}
                        alt={c.display_name}
                        className="w-12 h-12 rounded-full object-cover border border-[#C1D7E2]"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#C1D7E2]" />
                    )}
                    <div className="min-w-0">
                      <p className="font-serif text-lg text-[#7C9CB1] truncate">
                        {c.display_name}
                      </p>
                      <p className="font-mono text-[10px] text-[#90B0BF] truncate">
                        @{c.handle}
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 flex items-baseline justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#90B0BF]">
                      Earned
                    </span>
                    <span className="font-mono text-base text-[#7C9CB1]">
                      {Number(c.seva_earned || 0).toFixed(0)} SEVA
                    </span>
                  </div>
                  <div className="mt-2 flex items-baseline justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#90B0BF]">
                      Sales
                    </span>
                    <span className="font-mono text-sm text-[#7C9CB1]">
                      {c.sales_count}
                    </span>
                  </div>
                  <Link
                    to={`/dashboard?handle=${c.handle}`}
                    data-testid={`leader-${i}-dashboard`}
                    className="mt-5 inline-flex items-center justify-center font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C9CB1] border border-[#C1D7E2] py-2 hover:bg-[#C1D7E2]/40 transition-colors"
                  >
                    View dashboard
                  </Link>
                </article>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Feed */}
      <section
        id="feed"
        data-testid="sevagram-feed"
        className="px-6 md:px-12 py-20 md:py-28"
      >
        <div className="max-w-[1400px] mx-auto">
          <SectionLabel testId="feed-label">The Feed</SectionLabel>
          <h2 className="font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-[1.05] mb-12">
            Rituals, in the wild.
          </h2>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="aspect-square bg-[#C1D7E2]/40 animate-pulse" />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <p
              data-testid="feed-empty"
              className="font-serif italic text-[#7C9CB1] text-2xl py-20"
            >
              The feed is empty — be the first to post.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
              {posts.map((p, idx) => (
                <article
                  key={p.id}
                  data-testid={`post-${idx}`}
                  className="group"
                >
                  <div className="relative aspect-square overflow-hidden bg-[#F4F8F9] mb-4">
                    <img
                      src={p.media_url}
                      alt={p.caption}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                    {p.platform !== 'sevagram' && (
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1 font-mono text-[9px] tracking-[0.25em] uppercase text-[#7C9CB1] bg-[#F4F8F9]/85 backdrop-blur-md px-2 py-1 border border-[#C1D7E2]">
                        {p.platform === 'instagram' ? (
                          <Instagram size={10} strokeWidth={1.5} />
                        ) : (
                          <Music2 size={10} strokeWidth={1.5} />
                        )}
                        {p.platform}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-serif text-lg text-[#7C9CB1]">
                      @{p.creator_handle}
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.25em] text-[#90B0BF]">
                      · {new Date(p.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  <p className="text-[#7C9CB1] font-light leading-relaxed mb-4">
                    {p.caption}
                  </p>

                  <div className="flex items-center justify-between flex-wrap gap-3 border-t border-[#C1D7E2] pt-4">
                    <div className="flex items-center gap-3">
                      <ShareMenu post={p} />
                      {p.external_url && (
                        <a
                          href={p.external_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => onPostClick(p)}
                          data-testid={`post-external-${idx}`}
                          className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] uppercase text-[#7C9CB1] hover:text-[#90B0BF]"
                        >
                          View <ExternalLink size={11} strokeWidth={1.5} />
                        </a>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        onPostClick(p);
                        setOrderRef({
                          ref: p.id,
                          productSlugs: p.product_slugs,
                          creatorHandle: p.creator_handle,
                        });
                      }}
                      data-testid={`post-order-${idx}`}
                      className="inline-flex items-center gap-2 bg-[#7C9CB1] text-[#F4F8F9] px-4 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#90B0BF] transition-colors"
                    >
                      Order with this
                    </button>
                  </div>

                  <div className="mt-3 flex items-center gap-3 text-[#90B0BF] flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Eye size={13} strokeWidth={1.5} />
                      <span className="font-mono text-[10px] tracking-[0.2em]">
                        {p.views || 0}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Heart size={13} strokeWidth={1.5} />
                      <span className="font-mono text-[10px] tracking-[0.2em]">
                        {p.likes || 0}
                      </span>
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.2em]">
                      · {p.orders_attributed} orders · {Number(p.seva_generated || 0).toFixed(2)} SEVA generated
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Apply form */}
      <section
        id="apply"
        data-testid="sevagram-apply"
        className="px-6 md:px-12 py-24 md:py-32 bg-[#7C9CB1] text-[#F4F8F9]"
      >
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/80 mb-4">
              <span className="inline-block w-7 h-px bg-[#F4F8F9]/80 mr-3 align-middle" />
              Become a creator
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05]">
              Lend your voice.
              <br />
              Earn $SEVA forever.
            </h2>
            <p className="mt-6 text-[#F4F8F9]/90 font-light leading-relaxed max-w-md">
              Apply with your handle, your channels, and a few words about your
              ritual. Approved creators receive a permanent ref code and start
              earning on every attributed order.
            </p>
          </div>
          <ApplyCreatorForm />
        </div>
      </section>

      {orderRef && (
        <OrderModal
          attributionRef={orderRef.ref}
          initialProductSlugs={orderRef.productSlugs}
          creatorHandle={orderRef.creatorHandle}
          onClose={() => setOrderRef(null)}
        />
      )}
    </div>
  );
};

export default SevagramPage;
